/**
 * SocialMediaBrandingTab Component
 * Manages social media branding settings including handles, hashtags, and watermarks
 */

import React, { useState } from 'react';
import {
  Share2,
  Hash,
  AtSign,
  Image,
  Link2,
  AlertCircle,
  RefreshCw,
  Save,
  Plus,
  X,
  Facebook,
  Instagram,
  Twitter,
  Linkedin,
  Cloud,
  FileText,
} from 'lucide-react';
import { OrganizationBranding } from '@/services/api/branding';

interface SocialMediaBrandingTabProps {
  branding: Partial<OrganizationBranding>;
  onChange: (updates: Partial<OrganizationBranding>) => void;
  onSave: () => Promise<void>;
  saving: boolean;
  organizationName: string;
}

const WATERMARK_POSITIONS = [
  { value: 'top-left', label: 'Top Left' },
  { value: 'top-right', label: 'Top Right' },
  { value: 'bottom-left', label: 'Bottom Left' },
  { value: 'bottom-right', label: 'Bottom Right' },
  { value: 'center', label: 'Center' },
];

export function SocialMediaBrandingTab({
  branding,
  onChange,
  onSave,
  saving,
  organizationName,
}: SocialMediaBrandingTabProps) {
  const [newHashtag, setNewHashtag] = useState('');

  const handleChange = (field: keyof OrganizationBranding, value: any) => {
    onChange({ [field]: value });
  };

  const addHashtag = () => {
    if (!newHashtag.trim()) return;

    const tag = newHashtag.startsWith('#') ? newHashtag : `#${newHashtag}`;
    const currentTags = branding.social_default_hashtags || [];

    if (!currentTags.includes(tag)) {
      handleChange('social_default_hashtags', [...currentTags, tag]);
    }
    setNewHashtag('');
  };

  const removeHashtag = (tag: string) => {
    const currentTags = branding.social_default_hashtags || [];
    handleChange(
      'social_default_hashtags',
      currentTags.filter((t) => t !== tag)
    );
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addHashtag();
    }
  };

  return (
    <div className="space-y-8">
      {/* Display Name & Bio */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-center mb-6">
          <Share2 className="w-5 h-5 mr-2 text-blue-600" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">Social Profile</h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Display Name
            </label>
            <input
              type="text"
              value={branding.social_display_name || ''}
              onChange={(e) => handleChange('social_display_name', e.target.value)}
              placeholder={organizationName}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
            <p className="text-xs text-gray-500 mt-1">
              Display name for social media posts
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Link in Bio URL
            </label>
            <input
              type="url"
              value={branding.social_link_in_bio_url || ''}
              onChange={(e) => handleChange('social_link_in_bio_url', e.target.value)}
              placeholder="https://yoursite.com"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
            <p className="text-xs text-gray-500 mt-1">
              Primary link for social profiles
            </p>
          </div>
        </div>

        <div className="mt-6">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Bio Template
          </label>
          <textarea
            value={branding.social_bio_template || ''}
            onChange={(e) => handleChange('social_bio_template', e.target.value)}
            rows={3}
            placeholder="Helping communities thrive. Learn more at {{website}}"
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          />
          <p className="text-xs text-gray-500 mt-1">
            Available variables: {'{{website}}'}, {'{{phone}}'}, {'{{email}}'}
          </p>
        </div>
      </div>

      {/* Social Handles */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-center mb-6">
          <AtSign className="w-5 h-5 mr-2 text-purple-600" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">Social Handles</h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="flex items-center">
            <div className="p-2 bg-blue-600 rounded-lg mr-3">
              <Facebook className="w-5 h-5 text-white" />
            </div>
            <div className="flex-1">
              <label className="block text-xs text-gray-500 mb-1">Facebook</label>
              <div className="flex">
                <span className="px-3 py-2 bg-gray-100 dark:bg-gray-700 border border-r-0 border-gray-300 dark:border-gray-600 rounded-l-lg text-gray-500 text-sm">
                  facebook.com/
                </span>
                <input
                  type="text"
                  value={branding.social_handle_facebook || ''}
                  onChange={(e) => handleChange('social_handle_facebook', e.target.value)}
                  placeholder="yourpage"
                  className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-r-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>
            </div>
          </div>

          <div className="flex items-center">
            <div className="p-2 bg-gradient-to-r from-purple-600 to-pink-500 rounded-lg mr-3">
              <Instagram className="w-5 h-5 text-white" />
            </div>
            <div className="flex-1">
              <label className="block text-xs text-gray-500 mb-1">Instagram</label>
              <div className="flex">
                <span className="px-3 py-2 bg-gray-100 dark:bg-gray-700 border border-r-0 border-gray-300 dark:border-gray-600 rounded-l-lg text-gray-500 text-sm">
                  @
                </span>
                <input
                  type="text"
                  value={branding.social_handle_instagram || ''}
                  onChange={(e) => handleChange('social_handle_instagram', e.target.value)}
                  placeholder="yourhandle"
                  className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-r-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>
            </div>
          </div>

          <div className="flex items-center">
            <div className="p-2 bg-black rounded-lg mr-3">
              <Twitter className="w-5 h-5 text-white" />
            </div>
            <div className="flex-1">
              <label className="block text-xs text-gray-500 mb-1">X (Twitter)</label>
              <div className="flex">
                <span className="px-3 py-2 bg-gray-100 dark:bg-gray-700 border border-r-0 border-gray-300 dark:border-gray-600 rounded-l-lg text-gray-500 text-sm">
                  @
                </span>
                <input
                  type="text"
                  value={branding.social_handle_twitter || ''}
                  onChange={(e) => handleChange('social_handle_twitter', e.target.value)}
                  placeholder="yourhandle"
                  className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-r-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>
            </div>
          </div>

          <div className="flex items-center">
            <div className="p-2 bg-blue-700 rounded-lg mr-3">
              <Linkedin className="w-5 h-5 text-white" />
            </div>
            <div className="flex-1">
              <label className="block text-xs text-gray-500 mb-1">LinkedIn</label>
              <div className="flex">
                <span className="px-3 py-2 bg-gray-100 dark:bg-gray-700 border border-r-0 border-gray-300 dark:border-gray-600 rounded-l-lg text-gray-500 text-sm">
                  linkedin.com/company/
                </span>
                <input
                  type="text"
                  value={branding.social_handle_linkedin || ''}
                  onChange={(e) => handleChange('social_handle_linkedin', e.target.value)}
                  placeholder="yourcompany"
                  className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-r-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>
            </div>
          </div>

          <div className="flex items-center">
            <div className="p-2 bg-sky-500 rounded-lg mr-3">
              <Cloud className="w-5 h-5 text-white" />
            </div>
            <div className="flex-1">
              <label className="block text-xs text-gray-500 mb-1">Bluesky</label>
              <div className="flex">
                <span className="px-3 py-2 bg-gray-100 dark:bg-gray-700 border border-r-0 border-gray-300 dark:border-gray-600 rounded-l-lg text-gray-500 text-sm">
                  @
                </span>
                <input
                  type="text"
                  value={branding.social_handle_bluesky || ''}
                  onChange={(e) => handleChange('social_handle_bluesky', e.target.value)}
                  placeholder="you.bsky.social"
                  className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-r-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Default Hashtags */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-center mb-6">
          <Hash className="w-5 h-5 mr-2 text-green-600" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">Default Hashtags</h3>
        </div>

        <p className="text-sm text-gray-500 mb-4">
          These hashtags will be automatically added to your social media campaigns.
        </p>

        <div className="flex flex-wrap gap-2 mb-4">
          {(branding.social_default_hashtags || []).map((tag) => (
            <span
              key={tag}
              className="inline-flex items-center px-3 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 rounded-full text-sm"
            >
              {tag}
              <button
                onClick={() => removeHashtag(tag)}
                className="ml-2 hover:text-blue-600"
              >
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
        </div>

        <div className="flex">
          <input
            type="text"
            value={newHashtag}
            onChange={(e) => setNewHashtag(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Add a hashtag..."
            className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-l-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          />
          <button
            onClick={addHashtag}
            className="px-4 py-2 bg-blue-600 text-white rounded-r-lg hover:bg-blue-700"
          >
            <Plus className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Disclaimer */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-center mb-6">
          <AlertCircle className="w-5 h-5 mr-2 text-amber-600" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">Disclaimer Text</h3>
        </div>

        <textarea
          value={branding.social_disclaimer_text || ''}
          onChange={(e) => handleChange('social_disclaimer_text', e.target.value)}
          rows={3}
          placeholder="Views expressed are those of the organization and do not constitute medical or legal advice."
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
        />
        <p className="text-xs text-gray-500 mt-1">
          This disclaimer will be appended to social media posts when required.
        </p>
      </div>

      {/* Watermark Settings */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-center mb-6">
          <Image className="w-5 h-5 mr-2 text-pink-600" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">Watermark Settings</h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Watermark Image URL
            </label>
            <input
              type="text"
              value={branding.social_watermark_image_url || ''}
              onChange={(e) => handleChange('social_watermark_image_url', e.target.value)}
              placeholder="https://..."
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
            <p className="text-xs text-gray-500 mt-1">
              Recommended: PNG with transparent background
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Watermark Position
            </label>
            <select
              value={branding.social_watermark_position || 'bottom-right'}
              onChange={(e) => handleChange('social_watermark_position', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              {WATERMARK_POSITIONS.map((pos) => (
                <option key={pos.value} value={pos.value}>
                  {pos.label}
                </option>
              ))}
            </select>
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Watermark Opacity: {Math.round((branding.social_watermark_opacity || 0.7) * 100)}%
            </label>
            <input
              type="range"
              min="10"
              max="100"
              value={(branding.social_watermark_opacity || 0.7) * 100}
              onChange={(e) =>
                handleChange('social_watermark_opacity', parseInt(e.target.value) / 100)
              }
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer dark:bg-gray-700"
            />
          </div>
        </div>

        {/* Watermark Preview */}
        {branding.social_watermark_image_url && (
          <div className="mt-4">
            <p className="text-xs text-gray-500 mb-2">Preview</p>
            <div className="relative w-full max-w-md h-48 bg-gray-200 dark:bg-gray-700 rounded-lg overflow-hidden">
              <div className="absolute inset-0 flex items-center justify-center text-gray-400">
                Sample Image
              </div>
              <img
                src={branding.social_watermark_image_url}
                alt="Watermark"
                className={`absolute w-16 h-16 object-contain ${
                  branding.social_watermark_position === 'top-left' ? 'top-2 left-2' :
                  branding.social_watermark_position === 'top-right' ? 'top-2 right-2' :
                  branding.social_watermark_position === 'bottom-left' ? 'bottom-2 left-2' :
                  branding.social_watermark_position === 'center' ? 'top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2' :
                  'bottom-2 right-2'
                }`}
                style={{ opacity: branding.social_watermark_opacity || 0.7 }}
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = 'none';
                }}
              />
            </div>
          </div>
        )}
      </div>

      {/* Profile Images */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-center mb-6">
          <FileText className="w-5 h-5 mr-2 text-cyan-600" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">Profile Images</h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Profile Image URL
            </label>
            <input
              type="text"
              value={branding.social_profile_image_url || ''}
              onChange={(e) => handleChange('social_profile_image_url', e.target.value)}
              placeholder="https://..."
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
            <p className="text-xs text-gray-500 mt-1">
              Square image, minimum 400x400px
            </p>
            {branding.social_profile_image_url && (
              <img
                src={branding.social_profile_image_url}
                alt="Profile"
                className="mt-2 w-16 h-16 rounded-full object-cover border-2 border-gray-200"
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = 'none';
                }}
              />
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Cover Image URL
            </label>
            <input
              type="text"
              value={branding.social_cover_image_url || ''}
              onChange={(e) => handleChange('social_cover_image_url', e.target.value)}
              placeholder="https://..."
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
            <p className="text-xs text-gray-500 mt-1">
              Recommended: 1500x500px
            </p>
            {branding.social_cover_image_url && (
              <img
                src={branding.social_cover_image_url}
                alt="Cover"
                className="mt-2 w-full h-20 object-cover rounded border border-gray-200"
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = 'none';
                }}
              />
            )}
          </div>
        </div>
      </div>

      {/* Save Button */}
      <div className="flex justify-end">
        <button
          onClick={onSave}
          disabled={saving}
          className="flex items-center px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
        >
          {saving ? (
            <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Save className="w-4 h-4 mr-2" />
          )}
          Save Changes
        </button>
      </div>
    </div>
  );
}

export default SocialMediaBrandingTab;
